export interface SearchGeoGroupItem {
  search_id: string;
  status: string;
  created_at: string;
  properties_retrieved: number;
  sources_count: number;
  agencies_count: number;
  city: string;
  neighbourhood?: string;
}

export interface SearchGeoGroupInput {
  geo_key: string;
  city: string;
  neighbourhood?: string;
  searches: SearchGeoGroupItem[];
}

export interface HistoryFilterInput {
  text: string;
  status: string;
  sort: string;
}

/**
 * Returns searches matching the given status, or all when status is `all`.
 */
function filterByStatus(searches: SearchGeoGroupItem[], status: string): SearchGeoGroupItem[] {
  if (status === 'all') return searches;
  return searches.filter((s) => s.status === status);
}

/**
 * Keeps a group's searches only when the group's city/neighbourhood matches `text`.
 */
function filterByGeoText(
  searches: SearchGeoGroupItem[],
  group: SearchGeoGroupInput,
  text: string,
): SearchGeoGroupItem[] {
  if (!text) return searches;
  const lower = text.toLowerCase();
  const geoMatch =
    group.city.toLowerCase().includes(lower) ||
    (group.neighbourhood ?? '').toLowerCase().includes(lower);
  return geoMatch ? searches : [];
}

/**
 * Sorts searches by the requested order (`oldest`, `results`, or default recent).
 */
function sortSearches(searches: SearchGeoGroupItem[], sort: string): SearchGeoGroupItem[] {
  if (sort === 'oldest') {
    return [...searches].sort((a, b) => a.created_at.localeCompare(b.created_at));
  }
  if (sort === 'results') {
    return [...searches].sort((a, b) => b.properties_retrieved - a.properties_retrieved);
  }
  return searches;
}

/**
 * Filters and sorts partnership search history geo groups based on user criteria.
 *
 * Applies text search (city/neighbourhood match), status filter, and sort order.
 * Returns only groups that still have at least one search after filtering.
 */
export function applyHistoryFilters(
  groups: SearchGeoGroupInput[],
  filters: HistoryFilterInput,
): SearchGeoGroupInput[] {
  return groups
    .map((g) => {
      const byStatus = filterByStatus(g.searches, filters.status);
      const byGeo = filterByGeoText(byStatus, g, filters.text);
      const sorted = sortSearches(byGeo, filters.sort);
      return { ...g, searches: sorted };
    })
    .filter((g) => g.searches.length > 0);
}
